/**
 * NOTA 2026-05-07: o bloco "Tasks" deste store (tasks, taskTotals, fetchTasks,
 * loadMoreColumn, subscribeRealtime, moveTask, updateTask, agents, fetchAgents)
 * está ÓRFÃO desde o rebuild canônico do Kanban (`/kanban` agora usa
 * `useTasksBoard` em `src/components/raios/tasks/hooks/`). Mantido temporariamente
 * pra evitar PR-trem; deve ser removido em follow-up dedicado verificando que
 * MCActivityPage e ActivityDetailSheet não regridem (eles só consomem o bloco
 * Activity, que é mantido).
 */
import { create } from 'zustand';
import { api, type BackendTask } from '@/lib/api';
import { listAgentsCached, listSkillsCached, resetMetaCache } from '@/lib/meta-cache';
import { mapBackendToKanban, mapKanbanStatusToBackend } from '@/lib/task-mapping';
import { supabase } from '@/lib/supabase';
import type { MockTask, MockAgent } from '@/lib/mock-data';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type {
  ActivityItem,
  ActivityDetail,
  ActivityKind,
  ActivityStatus,
  AgentMeta,
  SkillMeta,
  PeriodFilter,
} from '@/lib/activity';
import { periodToSinceISO } from '@/lib/activity';

/* ── Tela Atividade ──────────────────────────────────────────────────────────
 * Cache de metadados (Maps) eh hidratado no mount via listAgents + listSkills
 * (refresh 5min). Realtime payload chega cru — normalizamos via cache (hit > 99%
 * em uso normal). Cache miss agenda batch lookup deduplicado (max 20 keys / 100ms),
 * nao bloqueia a linha (renderiza otimista com slug, atualiza inline ao resolver).
 *
 * Por que Zustand e nao React Query: precisamos mutar a lista in-place em INSERT/
 * UPDATE Realtime. React Query brilha em fetch caching, nao em delta updates ao vivo.
 */

export interface ActivityFilters {
  kind: 'all' | ActivityKind;
  status: 'all' | ActivityStatus;
  actor_slug: string | null;
  period: PeriodFilter;
  /** Opcao B: default true (so rows root). false expande LLM calls + tool calls. */
  onlyRoot: boolean;
}

export type ActivityChannelState = 'idle' | 'connecting' | 'joined' | 'disconnected' | 'error';

const ACTIVITY_PAGE_LIMIT = 20;
const ACTIVITY_MAX_IN_MEMORY = 200;
const META_REFRESH_MS = 5 * 60 * 1000;
const META_BATCH_WINDOW_MS = 100;
const META_BATCH_MAX_KEYS = 20;
/* W1.4 — agent_executions é tabela PARTICIONADA (relkind='p'): Supabase
 * Realtime postgres_changes NÃO suporta particionadas (a assinatura falha com
 * CHANNEL_ERROR → badge "Erro de conexão"). Cobertura de execuções de agent é
 * feita por POLLING INCREMENTAL do RPC get_activity_feed (api.listActivity)
 * enquanto Dashboard/Atividade estiverem montados, com merge+dedup por kind+id.
 * Incidente 2026-08-08: 12s sem backoff e fallback `all` virou retry storm e
 * saturou o Postgres. O polling agora é 30s, limitado, visibility-aware e usa
 * backoff exponencial até 5min. */
const ACTIVITY_POLL_MS = 30_000;
const ACTIVITY_POLL_MAX_BACKOFF_MS = 5 * 60_000;
const ACTIVITY_POLL_EMPTY_LOOKBACK_MS = 5 * 60_000;
const ACTIVITY_POLL_LIMIT = 50;

export type MCActivity = ActivityItem;

interface MCStore {
  /* ---- Tasks ---- */
  tasks: MockTask[];
  agents: MockAgent[];
  tasksLoading: boolean;
  agentsLoading: boolean;
  _realtimeChannel: RealtimeChannel | null;

  /* Kanban perf 2026-05-06 — paginação por coluna mantendo total real */
  /** True após fetchTasks completar AO MENOS UMA VEZ (sucesso ou falha).
   *  Render do Kanban espera isso pra evitar flash entre skeleton → "vazio" → cards. */
  tasksLoaded: boolean;
  /** Total de tasks no DB por status kanban (vem de /tasks/stats). Usado pra header da coluna. */
  taskTotals: Record<string, number>;
  /** Avg duration por handler em segundos (vem de /tasks/stats). Usado pra ETA card. */
  avgDurationByHandler: Record<string, number>;
  /** Quantos cards já estão carregados em memória por status kanban. */
  taskColumnLoaded: Record<string, number>;
  /** Loading state granular por coluna pra spinner do "Carregar mais". */
  taskColumnLoading: Record<string, boolean>;

  fetchTasks: () => Promise<void>;
  /** Carrega +20 cards pra uma coluna específica (append). */
  loadMoreColumn: (kanbanStatus: string) => Promise<void>;
  fetchAgents: () => void;
  moveTask: (taskId: string, newStatus: string) => void;
  updateTask: (taskId: string, patch: Partial<MockTask>) => void;
  subscribeRealtime: (tenantId: string) => void;
  unsubscribeRealtime: () => void;

  /* ---- Tela Atividade (novo) ---- */
  activity: ActivityItem[];
  activityLoading: boolean;
  activityCursor: { ts: string; id: string } | null;
  activityHasMore: boolean;
  activityFilters: ActivityFilters;
  activityChannelState: ActivityChannelState;
  selectedActivity: ActivityItem | null;
  selectedActivityDetail: ActivityDetail | null;
  selectedActivityDetailLoading: boolean;

  fetchActivity: (reset?: boolean) => Promise<void>;
  loadMoreActivity: () => Promise<void>;
  setActivityFilters: (partial: Partial<ActivityFilters>) => void;
  hydrateActivityMeta: () => Promise<void>;
  subscribeActivityRealtime: (tenantId: string) => Promise<void>;
  unsubscribeActivityRealtime: () => void;
  openActivityDetail: (item: ActivityItem) => Promise<void>;
  closeActivityDetail: () => void;
}

const REAL_AGENTS: MockAgent[] = [
  { id: '@pm', name: 'Morgan', role: 'Product Manager', handle: '@pm', status: 'active', ema_score: 0, tasks_wip: 0, cost: 0 },
  { id: '@po', name: 'Pax', role: 'Product Owner', handle: '@po', status: 'active', ema_score: 0, tasks_wip: 0, cost: 0 },
  { id: '@architect', name: 'Helio', role: 'System Architect', handle: '@architect', status: 'active', ema_score: 0, tasks_wip: 0, cost: 0 },
  { id: '@data-engineer', name: 'Dara', role: 'Database Architect', handle: '@data-engineer', status: 'active', ema_score: 0, tasks_wip: 0, cost: 0 },
  { id: '@dev', name: 'Dex', role: 'Full Stack Developer', handle: '@dev', status: 'active', ema_score: 0, tasks_wip: 0, cost: 0 },
  { id: '@qa', name: 'Quinn', role: 'QA Engineer', handle: '@qa', status: 'active', ema_score: 0, tasks_wip: 0, cost: 0 },
  { id: '@devops', name: 'Gage', role: 'Release Engineer', handle: '@devops', status: 'active', ema_score: 0, tasks_wip: 0, cost: 0 },
  { id: '@companion', name: 'Companion', role: 'Assistente Pessoal', handle: '@companion', status: 'active', ema_score: 0, tasks_wip: 0, cost: 0 },
];

/* ── Module-level cache + realtime state ─────────────────────────────────────
 * Maps e timers ficam fora do store (Zustand) porque sao mutaveis e nao precisam
 * disparar re-render. Vivem na vida do modulo (browser tab); cleanup no unmount.
 */

const _agentsMeta = new Map<string, AgentMeta>(); // chave: id (UUID) E agent_id (slug) — mesma referencia
const _skillsMeta = new Map<string, SkillMeta>(); // chave: id (UUID)
const _metaInflight = new Map<string, Promise<unknown>>();
let _metaHydratedAt: number | null = null;
let _metaRefreshTimer: ReturnType<typeof setInterval> | null = null;
let _activityChannel: RealtimeChannel | null = null;
const _activityChannels: {
  // W1.4: SEM channel de agent_executions — tabela particionada não é
  // suportada pelo Realtime; cobertura via polling incremental (abaixo).
  skill: RealtimeChannel | null;
  ephemeral: RealtimeChannel | null;
  // Reforma UX — handler_executions + companion_activity_log entrando na
  // publication do Realtime (backend). Assinaturas ADITIVAS, mesmo padrão.
  handler: RealtimeChannel | null;
  companion: RealtimeChannel | null;
} = { skill: null, ephemeral: null, handler: null, companion: null };
let _activityPollTimer: ReturnType<typeof setTimeout> | null = null;
let _activityPollInflight = false;
let _activityPollingActive = false;
let _activityPollFailures = 0;
let _activityVisibilityHandler: (() => void) | null = null;
let _missBatch: { agentKeys: Set<string>; skillIds: Set<string>; timer: ReturnType<typeof setTimeout> | null } = {
  agentKeys: new Set(),
  skillIds: new Set(),
  timer: null,
};

/* ── Helpers de normalizacao de payload Realtime ─────────────────────────────
 * Os payloads do Supabase Realtime trazem rows cruas (sem JOIN). Convertemos
 * para shape do RPC usando cache; cache miss enfileira batch lookup.
 */

function normalizeStatus(table: string, raw: string | null): ActivityStatus {
  if (!raw) return 'running';
  if (table === 'agent_executions') return raw as ActivityStatus;
  if (table === 'skill_executions') {
    if (raw === 'ok') return 'completed';
    if (raw === 'canceled') return 'cancelled';
    return raw as ActivityStatus;
  }
  // ephemeral
  if (raw === 'ok') return 'completed';
  return raw as ActivityStatus;
}

function computeDurationMs(row: Record<string, unknown>): number | null {
  if (typeof row.duration_ms === 'number') return row.duration_ms;
  const startedAt = row.started_at as string | undefined;
  const endedAt = (row.ended_at ?? row.completed_at) as string | undefined;
  if (!startedAt || !endedAt) return null;
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  return ms >= 0 ? ms : null;
}

function extractHierarchy(row: Record<string, unknown>, table: string): {
  parent_trace_id: string | null;
  root_trace_id: string | null;
  metadata_kind: 'turn_root' | 'llm_call' | 'tool_call' | null;
  llm_calls_count: number | null;
  tool_calls_count: number | null;
  target_slug: string | null;
  // PR-5: campos partials sempre nascem null no payload realtime; sao populados
  // por recalcRunningParents apos filhas chegarem em rajada.
  cost_usd_partial: null;
  tokens_input_partial: null;
  tokens_output_partial: null;
  children_completed_count: null;
} {
  const parentTraceId = (row.parent_trace_id as string | null) ?? null;
  const rootTraceId = (row.root_trace_id as string | null) ?? null;
  let metadataKind: 'turn_root' | 'llm_call' | 'tool_call' | null = null;
  let llmCallsCount: number | null = null;
  let toolCallsCount: number | null = null;
  if (table === 'agent_executions') {
    const md = row.metadata as Record<string, unknown> | null;
    const k = md?.['kind'];
    if (k === 'turn_root' || k === 'llm_call' || k === 'tool_call') metadataKind = k;
    const llmRaw = md?.['llm_calls_count'];
    if (typeof llmRaw === 'number') llmCallsCount = llmRaw;
    const toolRaw = md?.['tool_calls_count'];
    if (typeof toolRaw === 'number') toolCallsCount = toolRaw;
  }
  // Plano-tela-atividade-canonica-v2 PR-3: target_slug populado em
  // handler_executions (RPC v3 expoe). Realtime payload de handler_executions
  // ja vem com target_slug. Demais tables: null.
  const targetSlug =
    table === 'handler_executions' ? ((row.target_slug as string | null) ?? null) : null;
  return {
    parent_trace_id: parentTraceId,
    root_trace_id: rootTraceId,
    metadata_kind: metadataKind,
    llm_calls_count: llmCallsCount,
    tool_calls_count: toolCallsCount,
    target_slug: targetSlug,
    cost_usd_partial: null,
    tokens_input_partial: null,
    tokens_output_partial: null,
    children_completed_count: null,
  };
}

/**
 * Plano-tela-atividade-canonica-v2 PR-5 (realtime SUM dinamico).
 *
 * Map em memoria que acumula soma de filhas terminais por root_trace_id.
 * Populado quando handlePayload recebe filha (parent_trace_id != null) com
 * status terminal (completed/failed) — mesmo quando filtro `onlyRoot=true`
 * descarta a filha do array `activity` (pra nao poluir a tela).
 *
 * Pais running leem deste map via recalcRunningParents pra exibir partial.
 * Quando pai termina (status terminal), partial vira null (cost_usd oficial
 * do trigger DB ja esta na coluna).
 */
const _childrenSums = new Map<
  string,
  { cost: number; tin: number; tout: number; count: number; ids: Set<string> }
>();

function _accumChild(rootTraceId: string, child: ActivityItem) {
  let entry = _childrenSums.get(rootTraceId);
  if (!entry) {
    entry = { cost: 0, tin: 0, tout: 0, count: 0, ids: new Set() };
    _childrenSums.set(rootTraceId, entry);
  }
  // Idempotente: re-aplicar mesma filha (mesmo id) nao duplica.
  if (entry.ids.has(child.id)) return;
  entry.ids.add(child.id);
  entry.count++;
  if (child.cost_usd != null) entry.cost += child.cost_usd;
  if (child.tokens_input != null) entry.tin += child.tokens_input;
  if (child.tokens_output != null) entry.tout += child.tokens_output;
}

/**
 * Aplica partials acumulados em _childrenSums sobre activity[]. Pais running
 * com trace_id ganham campos *_partial; pais terminais ficam intocados (cost
 * oficial do trigger DB vale).
 *
 * Custo: O(N) varrendo activity[]; lookup O(1) no map.
 */
function recalcRunningParents(items: ActivityItem[]): ActivityItem[] {
  let mutated = false;
  const next = items.map((item) => {
    if (item.status !== 'running') {
      // Limpa partials se houver (pai terminou — cost_usd oficial vale).
      if (item.cost_usd_partial != null || item.children_completed_count != null) {
        mutated = true;
        return {
          ...item,
          cost_usd_partial: null,
          tokens_input_partial: null,
          tokens_output_partial: null,
          children_completed_count: null,
        };
      }
      return item;
    }
    if (!item.trace_id) return item;
    const sums = _childrenSums.get(item.trace_id);
    const cost = sums?.cost ?? null;
    const tin = sums?.tin ?? null;
    const tout = sums?.tout ?? null;
    const count = sums?.count ?? null;
    if (
      item.cost_usd_partial === cost &&
      item.tokens_input_partial === tin &&
      item.tokens_output_partial === tout &&
      item.children_completed_count === count
    ) {
      return item;
    }
    mutated = true;
    return {
      ...item,
      cost_usd_partial: cost,
      tokens_input_partial: tin,
      tokens_output_partial: tout,
      children_completed_count: count,
    };
  });
  return mutated ? next : items;
}

let _partialRecalcTimer: ReturnType<typeof setTimeout> | null = null;
function _schedulePartialRecalc() {
  // Debounce 200ms (Pre-mortem 6 — absorve rajada de filhas terminando em sequencia,
  // evita flicker quando 5+ agents da skill terminam em <1s).
  if (_partialRecalcTimer) clearTimeout(_partialRecalcTimer);
  _partialRecalcTimer = setTimeout(() => {
    _partialRecalcTimer = null;
    useMCStore.setState((state) => ({ activity: recalcRunningParents(state.activity) }));
  }, 200);
}

function normalizeRealtimePayload(table: string, row: Record<string, unknown>): ActivityItem {
  if (table === 'agent_executions') {
    const slug = row.agent_id as string | null;
    const cached = slug ? _agentsMeta.get(slug) : null;
    if (slug && !cached) scheduleMetaLookup({ agentKey: slug });
    return {
      kind: 'agent',
      id: row.id as string,
      actor_id: slug,
      actor_slug: cached?.agent_id ?? slug,
      actor_name: cached?.name ?? slug,
      actor_archetype: cached?.archetype ?? null,
      actor_role: cached?.role ?? null,
      actor_scope: null,
      parent_actor_id: null,
      parent_actor_slug: null,
      parent_actor_name: null,
      invoker_type: null,
      status: normalizeStatus(table, row.status as string | null),
      started_at: row.started_at as string,
      ended_at: (row.completed_at as string | null) ?? null,
      duration_ms: computeDurationMs(row),
      cost_usd: typeof row.cost_usd === 'number' ? row.cost_usd : Number(row.cost_usd) || null,
      tokens_input: (row.tokens_input as number | null) ?? null,
      tokens_output: (row.tokens_output as number | null) ?? null,
      model_used: (row.model_used as string | null) ?? null,
      task_id: (row.task_id as string | null) ?? null,
      trace_id: (row.trace_id as string | null) ?? null,
      error_message: (row.error_message as string | null) ?? null,
      ...extractHierarchy(row, table),
    };
  }
  if (table === 'skill_executions') {
    const skillId = row.skill_id as string | null;
    const skillCached = skillId ? _skillsMeta.get(skillId) : null;
    if (skillId && !skillCached) scheduleMetaLookup({ skillId });
    const parentId = row.invoker_agent_id as string | null;
    const parentCached = parentId ? _agentsMeta.get(parentId) : null;
    if (parentId && !parentCached) scheduleMetaLookup({ agentKey: parentId });
    let errMsg: string | null = null;
    const rawErr = row.error;
    if (rawErr !== null && rawErr !== undefined) {
      try {
        errMsg = typeof rawErr === 'string' ? rawErr : JSON.stringify(rawErr);
      } catch {
        errMsg = String(rawErr);
      }
    }
    return {
      kind: 'skill',
      id: row.id as string,
      actor_id: skillId,
      actor_slug: skillCached?.slug ?? skillId,
      actor_name: skillCached?.name ?? skillCached?.slug ?? skillId,
      actor_archetype: null,
      actor_role: null,
      actor_scope: skillCached?.scope ?? null,
      parent_actor_id: parentId,
      parent_actor_slug: parentCached?.slug ?? null,
      parent_actor_name: parentCached?.name ?? null,
      invoker_type: (row.invoker_type as string | null) ?? null,
      status: normalizeStatus(table, row.status as string | null),
      started_at: row.started_at as string,
      ended_at: (row.ended_at as string | null) ?? null,
      duration_ms: computeDurationMs(row),
      cost_usd: typeof row.cost_usd === 'number' ? row.cost_usd : Number(row.cost_usd) || null,
      tokens_input: (row.tokens_input as number | null) ?? null,
      tokens_output: (row.tokens_output as number | null) ?? null,
      model_used: null,
      task_id: ((row.parent_task_id ?? row.standalone_task_id) as string | null) ?? null,
      trace_id: (row.trace_id as string | null) ?? null,
      error_message: errMsg,
      ...extractHierarchy(row, table),
    };
  }
  if (table === 'handler_executions') {
    // Espelha handler_rows do RPC get_activity_feed v3 (actor = handler_name,
    // invoker_type = origin, error JSONB → string, target_slug exposto).
    let errMsg: string | null = null;
    const rawErr = row.error;
    if (rawErr !== null && rawErr !== undefined) {
      try {
        errMsg = typeof rawErr === 'string' ? rawErr : JSON.stringify(rawErr);
      } catch {
        errMsg = String(rawErr);
      }
    }
    const handlerName = (row.handler_name as string | null) ?? null;
    return {
      kind: 'handler',
      id: row.id as string,
      actor_id: row.handler_id != null ? String(row.handler_id) : null,
      actor_slug: handlerName,
      actor_name: handlerName,
      actor_archetype: null,
      actor_role: null,
      actor_scope: null,
      parent_actor_id: null,
      parent_actor_slug: null,
      parent_actor_name: null,
      invoker_type: (row.origin as string | null) ?? null,
      status: normalizeStatus(table, row.status as string | null),
      started_at: row.started_at as string,
      ended_at: (row.completed_at as string | null) ?? null,
      duration_ms: computeDurationMs(row),
      cost_usd: typeof row.cost_usd === 'number' ? row.cost_usd : Number(row.cost_usd) || null,
      tokens_input: (row.tokens_input as number | null) ?? null,
      tokens_output: (row.tokens_output as number | null) ?? null,
      model_used: null,
      task_id: (row.task_id as string | null) ?? null,
      trace_id: (row.trace_id as string | null) ?? null,
      error_message: errMsg,
      ...extractHierarchy(row, table),
    };
  }
  if (table === 'companion_activity_log') {
    // Espelha companion_rows do RPC v3: só action='agent_loop.turn_completed'
    // vira item (filtrado no handlePayload); status sempre completed;
    // cost/tokens/duration/model vivem em `details` JSONB.
    const details = (row.details ?? {}) as Record<string, unknown>;
    const num = (v: unknown): number | null => {
      if (typeof v === 'number') return Number.isFinite(v) ? v : null;
      if (typeof v === 'string' && v !== '' && Number.isFinite(Number(v))) return Number(v);
      return null;
    };
    return {
      kind: 'companion',
      id: row.id as string,
      actor_id: (row.companion_id as string | null) ?? null,
      actor_slug: '@companion',
      actor_name: '@companion',
      actor_archetype: null,
      actor_role: null,
      actor_scope: null,
      parent_actor_id: null,
      parent_actor_slug: null,
      parent_actor_name: null,
      invoker_type: 'companion-turn',
      status: 'completed',
      started_at: row.created_at as string,
      ended_at: (row.created_at as string | null) ?? null,
      duration_ms: num(details['duration_ms']),
      cost_usd: num(details['cost_usd']),
      tokens_input: num(details['input_tokens']),
      tokens_output: num(details['output_tokens']),
      model_used: typeof details['model_used'] === 'string' ? details['model_used'] : null,
      task_id: null,
      trace_id: (row.trace_id as string | null) ?? null,
      error_message: null,
      parent_trace_id: null,
      root_trace_id: (row.root_trace_id as string | null) ?? null,
      metadata_kind: null,
      llm_calls_count: null,
      tool_calls_count: null,
      target_slug: null,
      cost_usd_partial: null,
      tokens_input_partial: null,
      tokens_output_partial: null,
      children_completed_count: null,
    };
  }
  // ephemeral_executions
  const parentId = row.parent_agent_id as string | null;
  const parentCached = parentId ? _agentsMeta.get(parentId) : null;
  if (parentId && !parentCached) scheduleMetaLookup({ agentKey: parentId });
  const runnerType = row.runner_type as string;
  return {
    kind: 'ephemeral',
    id: row.id as string,
    actor_id: runnerType,
    actor_slug: runnerType,
    actor_name: runnerType,
    actor_archetype: null,
    actor_role: null,
    actor_scope: null,
    parent_actor_id: parentId,
    parent_actor_slug: parentCached?.slug ?? null,
    parent_actor_name: parentCached?.name ?? null,
    invoker_type: 'spawn',
    status: normalizeStatus(table, row.status as string | null),
    started_at: row.started_at as string,
    ended_at: (row.ended_at as string | null) ?? null,
    duration_ms: computeDurationMs(row),
    cost_usd: null,
    tokens_input: null,
    tokens_output: null,
    model_used: null,
    task_id: null,
    trace_id: (row.trace_id as string | null) ?? null,
    error_message: null,
    ...extractHierarchy(row, table),
  };
}

function scheduleMetaLookup(opts: { agentKey?: string; skillId?: string }): void {
  if (opts.agentKey) _missBatch.agentKeys.add(opts.agentKey);
  if (opts.skillId) _missBatch.skillIds.add(opts.skillId);
  if (_missBatch.timer) return;
  _missBatch.timer = setTimeout(() => {
    void flushMissBatch();
  }, META_BATCH_WINDOW_MS);
}

async function flushMissBatch(): Promise<void> {
  const agentKeys = Array.from(_missBatch.agentKeys).slice(0, META_BATCH_MAX_KEYS);
  const skillIds = Array.from(_missBatch.skillIds).slice(0, META_BATCH_MAX_KEYS);
  _missBatch = { agentKeys: new Set(), skillIds: new Set(), timer: null };

  const agentPromises = agentKeys.map(async (key) => {
    if (_agentsMeta.has(key)) return;
    const inflightKey = `agent:${key}`;
    const existing = _metaInflight.get(inflightKey);
    if (existing) return existing;
    const p = api
      .getAgent(key)
      .then((a) => {
        const meta: AgentMeta = {
          id: a.id,
          agent_id: (a as unknown as { agent_id?: string }).agent_id ?? a.slug,
          slug: a.slug,
          name: a.name,
          archetype: (a as unknown as { archetype?: string }).archetype ?? null,
          role: (a as unknown as { role?: string }).role ?? null,
        };
        _agentsMeta.set(meta.id, meta);
        _agentsMeta.set(meta.agent_id, meta);
        _agentsMeta.set(meta.slug, meta);
        patchActivityRowsByActorKey(key);
      })
      .catch(() => {})
      .finally(() => _metaInflight.delete(inflightKey));
    _metaInflight.set(inflightKey, p);
    return p;
  });

  const skillPromises = skillIds.map(async (id) => {
    if (_skillsMeta.has(id)) return;
    const inflightKey = `skill:${id}`;
    const existing = _metaInflight.get(inflightKey);
    if (existing) return existing;
    const p = api
      .getSkill(id)
      .then((s) => {
        const meta: SkillMeta = {
          id: s.id,
          slug: s.slug,
          name: s.name,
          scope: (s as unknown as { scope?: string }).scope ?? null,
        };
        _skillsMeta.set(meta.id, meta);
        patchActivityRowsByActorKey(id);
      })
      .catch(() => {})
      .finally(() => _metaInflight.delete(inflightKey));
    _metaInflight.set(inflightKey, p);
    return p;
  });

  await Promise.all([...agentPromises, ...skillPromises]);
}

function patchActivityRowsByActorKey(key: string): void {
  const state = useMCStore.getState();
  let changed = false;
  const patched = state.activity.map((item) => {
    if (item.actor_id === key || item.parent_actor_id === key) {
      changed = true;
      // Re-resolve via cache
      if (item.kind === 'agent') {
        const meta = _agentsMeta.get(item.actor_id ?? '');
        if (meta) {
          return {
            ...item,
            actor_slug: meta.agent_id,
            actor_name: meta.name,
            actor_archetype: meta.archetype,
            actor_role: meta.role,
          };
        }
      }
      if (item.kind === 'skill') {
        const skillMeta = item.actor_id ? _skillsMeta.get(item.actor_id) : null;
        const parentMeta = item.parent_actor_id ? _agentsMeta.get(item.parent_actor_id) : null;
        return {
          ...item,
          actor_slug: skillMeta?.slug ?? item.actor_slug,
          actor_name: skillMeta?.name ?? item.actor_name,
          actor_scope: skillMeta?.scope ?? item.actor_scope,
          parent_actor_slug: parentMeta?.slug ?? item.parent_actor_slug,
          parent_actor_name: parentMeta?.name ?? item.parent_actor_name,
        };
      }
      if (item.kind === 'ephemeral') {
        const parentMeta = item.parent_actor_id ? _agentsMeta.get(item.parent_actor_id) : null;
        return {
          ...item,
          parent_actor_slug: parentMeta?.slug ?? item.parent_actor_slug,
          parent_actor_name: parentMeta?.name ?? item.parent_actor_name,
        };
      }
    }
    return item;
  });
  if (changed) useMCStore.setState({ activity: patched });
}

function clampActivity(items: ActivityItem[]): ActivityItem[] {
  if (items.length <= ACTIVITY_MAX_IN_MEMORY) return items;
  return items.slice(0, ACTIVITY_MAX_IN_MEMORY);
}

/* ── W1.4 — Polling incremental do feed (cobre agent_executions) ─────────────
 * agent_executions é particionada → sem postgres_changes. A cada 12s buscamos
 * SÓ itens novos (started_at >= último visto) no RPC get_activity_feed e
 * mergeamos no feed com dedup por kind+id. Polling ativo NÃO é erro: o estado
 * do canal (badge) reflete exclusivamente as assinaturas realtime SUPORTADAS.
 */

/** Campos mutáveis de uma execução — se nenhum mudou, preserva a referência. */
function activityItemChanged(a: ActivityItem, b: ActivityItem): boolean {
  return (
    a.status !== b.status ||
    a.ended_at !== b.ended_at ||
    a.duration_ms !== b.duration_ms ||
    a.cost_usd !== b.cost_usd ||
    a.tokens_input !== b.tokens_input ||
    a.tokens_output !== b.tokens_output ||
    a.error_message !== b.error_message ||
    a.llm_calls_count !== b.llm_calls_count ||
    a.tool_calls_count !== b.tool_calls_count
  );
}

/**
 * Merge de itens novos/atualizados do polling sobre o feed em memória.
 * Dedup por `kind:id` (id sozinho pode colidir entre tabelas). Retorna a MESMA
 * referência quando nada mudou (evita re-render a cada tick — o RPC
 * re-retorna os itens do watermark).
 */
function mergeActivityFeedItems(current: ActivityItem[], incoming: ActivityItem[]): ActivityItem[] {
  if (incoming.length === 0) return current;
  const byKey = new Map<string, ActivityItem>(current.map((i) => [`${i.kind}:${i.id}`, i]));
  let changed = false;
  for (const item of incoming) {
    const key = `${item.kind}:${item.id}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, item);
      changed = true;
    } else if (activityItemChanged(existing, item)) {
      // Preserva partials calculados client-side (PR-5) — o RPC não os traz.
      byKey.set(key, {
        ...item,
        cost_usd_partial: existing.cost_usd_partial,
        tokens_input_partial: existing.tokens_input_partial,
        tokens_output_partial: existing.tokens_output_partial,
        children_completed_count: existing.children_completed_count,
      });
      changed = true;
    }
  }
  if (!changed) return current;
  const next = [...byKey.values()].sort((a, b) => {
    if (a.started_at !== b.started_at) return a.started_at < b.started_at ? 1 : -1;
    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
  });
  return clampActivity(next);
}

/**
 * Um tick do polling incremental. `since` = started_at mais recente em memória;
 * recua até a execução de AGENT ainda `running` mais antiga para capturar a
 * transição de status (sem realtime, o UPDATE terminal só chega re-buscando).
 * Nunca consulta sem limite: se o feed ainda está vazio, o watermark mínimo é
 * agora-5min (a busca histórica `all` continua disponível apenas no fetch
 * explícito da tela). Retorna sucesso/falha para o scheduler aplicar backoff.
 */
async function pollActivityFeedOnce(): Promise<boolean> {
  if (_activityPollInflight) return true;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return true;
  _activityPollInflight = true;
  try {
    const state = useMCStore.getState();
    const filters = state.activityFilters;
    let since: string | null = state.activity[0]?.started_at ?? null;
    for (const it of state.activity) {
      if (it.kind === 'agent' && it.status === 'running' && (since === null || it.started_at < since)) {
        since = it.started_at;
      }
    }
    // REGRA DE BLAST RADIUS: polling automático jamais usa p_since=null.
    // `period=all` é uma escolha legítima para navegação manual, não para um
    // timer infinito. Sem watermark, olha somente os últimos 5 minutos.
    const boundedSince = since ?? new Date(Date.now() - ACTIVITY_POLL_EMPTY_LOOKBACK_MS).toISOString();
    const resp = await api.listActivity({
      limit: ACTIVITY_POLL_LIMIT,
      kind: filters.kind === 'all' ? undefined : filters.kind,
      status: filters.status === 'all' ? undefined : filters.status,
      actor_slug: filters.actor_slug ?? undefined,
      since: boundedSince,
      only_root: filters.onlyRoot,
    });
    if (resp.items.length === 0) return true;
    useMCStore.setState((curr) => {
      const merged = mergeActivityFeedItems(curr.activity, resp.items);
      return merged === curr.activity ? {} : { activity: merged };
    });
    // Pais running podem ter terminado neste tick → recalc limpa/atualiza partials.
    _schedulePartialRecalc();
    return true;
  } catch (err) {
    console.warn('activity poll: erro nao-fatal; próximo tick usa backoff', err);
    return false;
  } finally {
    _activityPollInflight = false;
  }
}

function scheduleActivityPoll(delayMs: number): void {
  if (!_activityPollingActive) return;
  if (_activityPollTimer) clearTimeout(_activityPollTimer);
  _activityPollTimer = setTimeout(async () => {
    _activityPollTimer = null;
    if (!_activityPollingActive) return;
    const succeeded = await pollActivityFeedOnce();
    _activityPollFailures = succeeded ? 0 : Math.min(_activityPollFailures + 1, 8);
    const nextDelay = succeeded
      ? ACTIVITY_POLL_MS
      : Math.min(ACTIVITY_POLL_MAX_BACKOFF_MS, ACTIVITY_POLL_MS * 2 ** _activityPollFailures);
    scheduleActivityPoll(nextDelay);
  }, Math.max(0, delayMs));
}

function startActivityPolling(): void {
  stopActivityPolling();
  _activityPollingActive = true;
  _activityPollFailures = 0;
  if (typeof document !== 'undefined') {
    _activityVisibilityHandler = () => {
      if (document.visibilityState !== 'visible' || !_activityPollingActive) return;
      // Retorno de background: espera 5s para evitar rajada simultânea de abas.
      scheduleActivityPoll(5_000);
    };
    document.addEventListener('visibilitychange', _activityVisibilityHandler);
  }
  scheduleActivityPoll(ACTIVITY_POLL_MS);
}

function stopActivityPolling(): void {
  _activityPollingActive = false;
  if (_activityPollTimer) {
    clearTimeout(_activityPollTimer);
    _activityPollTimer = null;
  }
  if (typeof document !== 'undefined' && _activityVisibilityHandler) {
    document.removeEventListener('visibilitychange', _activityVisibilityHandler);
  }
  _activityVisibilityHandler = null;
  _activityPollFailures = 0;
}

/* ── Store ───────────────────────────────────────────────────────────────── */

/**
 * Kanban perf 2026-05-06 — page size por coluna.
 * Backend aceita até 500; mantemos 20 pra payload pequeno + render rápido.
 * `Carregar mais` no footer da coluna append +20.
 */
const KANBAN_PAGE_SIZE = 20;

// Kanban perf 2026-05-06 — alinhado com COLUMNS em MCTasksPage. Mudanças
// aqui DEVEM espelhar as colunas renderizadas pra não dar gaps de
// taskTotals (count real do DB por coluna).
const KANBAN_STATUSES: Array<MockTask['status']> = [
  'pendente',
  'em_progresso',
  'paused',
  'em_revisao',
  'aprovado',
  'concluido',
  'bloqueado',
  'cancelado',
];

export const useMCStore = create<MCStore>((set, get) => ({
  tasks: [],
  agents: REAL_AGENTS,
  tasksLoading: true,
  // tasksLoaded fica false até fetchTasks completar (sucesso OU falha) UMA vez.
  // Render do Kanban usa isso (não tasksLoading) pra decidir skeleton vs conteúdo.
  // Evita transição skeleton → "vazio" → cards que admin reportou.
  tasksLoaded: false,
  agentsLoading: false,
  _realtimeChannel: null,
  taskTotals: {},
  avgDurationByHandler: {},
  taskColumnLoaded: {},
  taskColumnLoading: {},

  /* Tela Atividade — initial state */
  activity: [],
  activityLoading: false,
  activityCursor: null,
  activityHasMore: false,
  activityFilters: {
    // Default 'so turnos' (Opcao D): turn_root agora carrega modelo + cost + Σtokens
    // + counts agregados (llm_calls_count, tool_calls_count em metadata), entao 1
    // linha por turno eh auto-suficiente. Drill-down de LLM calls/tool calls via
    // Sheet (click no row) ou via toggle "Tudo" no segmented control.
    onlyRoot: true,
    kind: 'all',
    status: 'all',
    actor_slug: null,
    // Consulta inicial limitada ao dia atual. Histórico completo continua
    // disponível por seleção explícita; nunca é default de tela/polling.
    period: 'today',
  },
  activityChannelState: 'idle',
  selectedActivity: null,
  selectedActivityDetail: null,
  selectedActivityDetailLoading: false,

  fetchTasks: async () => {
    set({ tasksLoading: true });
    try {
      // Kanban perf 2026-05-06 — busca stats + 1ª página de cada coluna em
      // paralelo. Antes pegava 200 tasks num único request. Agora puxa só
      // ~20 por coluna (6 colunas → ~120 max) + counts agregados pra header.
      const [stats, ...pages] = await Promise.all([
        api.getTaskStats().catch((err) => {
          console.error('Failed to fetch task stats:', err);
          return { counts: {}, avg_duration_seconds_by_handler: {} } as const;
        }),
        ...KANBAN_STATUSES.map((kanbanStatus) =>
          api.listTasks({
            status: mapKanbanStatusToBackend(kanbanStatus),
            limit: KANBAN_PAGE_SIZE,
            offset: 0,
          }),
        ),
      ]);

      // taskTotals = stats.counts (count exact do DB por status). UMA fonte.
      // Se algum backend status não tiver entry no stats, fica 0 (não há tasks
      // daquele status no DB).
      const taskTotals: Record<string, number> = {};
      const taskColumnLoaded: Record<string, number> = {};
      const allBackendTasks: BackendTask[] = [];
      const statsCounts: Record<string, number> = stats.counts ?? {};
      pages.forEach((page, idx) => {
        const kanbanStatus = KANBAN_STATUSES[idx]!;
        const items = page.items.filter((t) => t.event_type !== 'user.request.companion');
        const backendStatus = mapKanbanStatusToBackend(kanbanStatus);
        taskTotals[kanbanStatus] = statsCounts[backendStatus] ?? 0;
        taskColumnLoaded[kanbanStatus] = items.length;
        allBackendTasks.push(...items);
      });

      // Debug temporário (remover após confirmar comportamento).
      // eslint-disable-next-line no-console
      console.info('[mc-store] fetchTasks', { statsCounts, taskTotals });

      // Subtask counts agregados — só consideram os carregados (parent visível
      // sem todos os filhos visíveis ainda fica com count parcial; trade-off
      // aceitável pra evitar query extra pesada).
      const parentCounts = new Map<string, { total: number; done: number }>();
      for (const t of allBackendTasks) {
        if (t.parent_task_id) {
          const counts = parentCounts.get(t.parent_task_id) ?? { total: 0, done: 0 };
          counts.total++;
          // API retorna lowercase. Comparação tolerante.
          if (String(t.status).toLowerCase() === 'done') counts.done++;
          parentCounts.set(t.parent_task_id, counts);
        }
      }

      const mapped = allBackendTasks.map((t) => {
        const kanban = mapBackendToKanban(t);
        const subtaskCounts = parentCounts.get(t.id);
        if (subtaskCounts) kanban.subtasks = subtaskCounts;
        return kanban;
      });

      set({
        tasks: mapped,
        taskTotals,
        taskColumnLoaded,
        avgDurationByHandler: stats.avg_duration_seconds_by_handler ?? {},
        tasksLoading: false,
        tasksLoaded: true,
      });
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
      // tasksLoaded fica true mesmo em falha — render mostra EmptyState
      // ("nenhuma task" + retry hint) em vez de skeleton infinito.
      set({ tasksLoading: false, tasksLoaded: true });
    }
  },

  loadMoreColumn: async (kanbanStatus: string) => {
    const state = get();
    if (state.taskColumnLoading[kanbanStatus]) return; // dedupe
    const loaded = state.taskColumnLoaded[kanbanStatus] ?? 0;
    const total = state.taskTotals[kanbanStatus] ?? 0;
    if (loaded >= total) return; // nothing left

    set({ taskColumnLoading: { ...state.taskColumnLoading, [kanbanStatus]: true } });
    try {
      const page = await api.listTasks({
        status: mapKanbanStatusToBackend(kanbanStatus),
        limit: KANBAN_PAGE_SIZE,
        offset: loaded,
      });
      const items = page.items.filter((t) => t.event_type !== 'user.request.companion');
      const mapped = items.map((t) => mapBackendToKanban(t));
      set((curr) => ({
        tasks: [...curr.tasks, ...mapped],
        taskColumnLoaded: {
          ...curr.taskColumnLoaded,
          [kanbanStatus]: (curr.taskColumnLoaded[kanbanStatus] ?? 0) + items.length,
        },
        taskTotals: { ...curr.taskTotals, [kanbanStatus]: page.total },
        taskColumnLoading: { ...curr.taskColumnLoading, [kanbanStatus]: false },
      }));
    } catch (err) {
      console.error(`Failed to load more for ${kanbanStatus}:`, err);
      set((curr) => ({
        taskColumnLoading: { ...curr.taskColumnLoading, [kanbanStatus]: false },
      }));
    }
  },

  fetchAgents: () => {
    /* static for now */
  },

  moveTask: (taskId, newStatus) => {
    set((state) => ({
      tasks: state.tasks.map((t) =>
        t.id === taskId ? { ...t, status: newStatus as MockTask['status'] } : t,
      ),
    }));
    const backendStatus = mapKanbanStatusToBackend(newStatus);
    api.transitionTask(taskId, backendStatus).catch((err) => {
      console.error('Failed to transition task:', err);
      get().fetchTasks();
    });
  },

  updateTask: (taskId, patch) => {
    set((state) => ({
      tasks: state.tasks.map((t) => (t.id === taskId ? { ...t, ...patch } : t)),
    }));
  },

  subscribeRealtime: (tenantId: string) => {
    const existing = get()._realtimeChannel;
    if (existing) supabase.removeChannel(existing);

    const channel = supabase
      .channel('tasks-realtime')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks',
          filter: `tenant_id=eq.${tenantId}`,
        },
        (payload) => {
          const task = payload.new as BackendTask;
          if (!task?.id) return;
          if (task.event_type === 'user.request.companion') return;
          const kanbanTask = mapBackendToKanban(task);
          set((state) => {
            const exists = state.tasks.find((t) => t.id === task.id);
            if (exists) {
              return {
                tasks: state.tasks.map((t) =>
                  t.id === task.id ? { ...kanbanTask, subtasks: t.subtasks } : t,
                ),
              };
            }
            return { tasks: [kanbanTask, ...state.tasks] };
          });
        },
      )
      .subscribe();

    set({ _realtimeChannel: channel });
  },

  unsubscribeRealtime: () => {
    const channel = get()._realtimeChannel;
    if (channel) {
      supabase.removeChannel(channel);
      set({ _realtimeChannel: null });
    }
  },

  /* ── Tela Atividade methods ────────────────────────────────────────────── */

  fetchActivity: async (reset = true) => {
    const filters = get().activityFilters;
    const since = periodToSinceISO(filters.period);
    set({ activityLoading: true });
    try {
      const cursor = reset ? null : get().activityCursor;
      const resp = await api.listActivity({
        limit: ACTIVITY_PAGE_LIMIT,
        kind: filters.kind === 'all' ? undefined : filters.kind,
        status: filters.status === 'all' ? undefined : filters.status,
        actor_slug: filters.actor_slug ?? undefined,
        since: since ?? undefined,
        cursor_ts: cursor?.ts,
        cursor_id: cursor?.id,
        only_root: filters.onlyRoot,
      });
      set((state) => ({
        activity: reset ? resp.items : clampActivity([...state.activity, ...resp.items]),
        activityCursor: resp.next_cursor,
        activityHasMore: resp.next_cursor !== null,
        activityLoading: false,
      }));
    } catch (err) {
      console.error('Failed to fetch activity:', err);
      set({ activityLoading: false });
    }
  },

  loadMoreActivity: async () => {
    if (!get().activityHasMore || get().activityLoading) return;
    await get().fetchActivity(false);
  },

  setActivityFilters: (partial) => {
    set((state) => ({
      activityFilters: { ...state.activityFilters, ...partial },
    }));
    void get().fetchActivity(true);
  },

  hydrateActivityMeta: async () => {
    try {
      // DEM-1933: fetchers CACHEADOS (TTL 60s + dedup in-flight) — divide as
      // requests com o fallback de counts da DashboardPage em vez de duplicar
      // listAgents/listSkills no mesmo mount.
      const [agents, skills] = await Promise.all([
        listAgentsCached().catch(() => []),
        listSkillsCached().catch(() => []),
      ]);
      for (const a of agents) {
        const meta: AgentMeta = {
          id: a.id,
          agent_id: (a as unknown as { agent_id?: string }).agent_id ?? a.slug,
          slug: a.slug,
          name: a.name,
          archetype: (a as unknown as { archetype?: string }).archetype ?? null,
          role: (a as unknown as { role?: string }).role ?? null,
        };
        _agentsMeta.set(meta.id, meta);
        _agentsMeta.set(meta.agent_id, meta);
        _agentsMeta.set(meta.slug, meta);
      }
      for (const s of skills) {
        const meta: SkillMeta = {
          id: s.id,
          slug: s.slug,
          name: s.name,
          scope: (s as unknown as { scope?: string }).scope ?? null,
        };
        _skillsMeta.set(meta.id, meta);
      }
      _metaHydratedAt = Date.now();
    } catch (err) {
      console.warn('hydrateActivityMeta: erro nao-fatal', err);
    }
  },

  subscribeActivityRealtime: async (tenantId: string) => {
    // DEM-1933: não bloquear a assinatura realtime no hydrateActivityMeta.
    // O hydrate faz listAgents/listSkills; antes o caminho crítico de atividade
    // ficava esperando essas 2 requests antes de abrir canais/polling. Dispara
    // em background e deixa o feed inicial/assinatura seguirem imediatamente.
    if (!_metaHydratedAt) {
      void get().hydrateActivityMeta();
    }
    if (_metaRefreshTimer) clearInterval(_metaRefreshTimer);
    _metaRefreshTimer = setInterval(() => {
      void get().hydrateActivityMeta();
    }, META_REFRESH_MS);

    if (_activityChannel) {
      supabase.removeChannel(_activityChannel);
      _activityChannel = null;
    }
    // Idempotência real: uma segunda assinatura (troca de tenant/StrictMode)
    // remove os channels anteriores antes de substituí-los. Sem isso, os
    // handles antigos continuavam vivos mesmo com apenas um timer de polling.
    for (const key of ['skill', 'ephemeral', 'handler', 'companion'] as const) {
      const existing = _activityChannels[key];
      if (existing) {
        supabase.removeChannel(existing);
        _activityChannels[key] = null;
      }
    }

    set({ activityChannelState: 'connecting' });

    const handlePayload = (table: string) => (payload: { eventType: string; new?: unknown; old?: unknown }) => {
      const row = payload.new as Record<string, unknown> | undefined;
      if (!row?.id) return;
      // companion_activity_log: só o turno completo vira item de feed (mesmo
      // filtro do RPC get_activity_feed — demais actions são ruído interno).
      if (table === 'companion_activity_log' && row.action !== 'agent_loop.turn_completed') {
        return;
      }
      const item = normalizeRealtimePayload(table, row);
      // PR-5 (realtime SUM): se a row eh filha terminal (parent_trace_id !=
      // null E status terminal), acumula em _childrenSums pra agregar partial
      // do pai — INDEPENDENTE do filtro onlyRoot que descarta a filha do
      // array `activity` visivel.
      if (
        item.parent_trace_id &&
        item.root_trace_id &&
        (item.status === 'completed' || item.status === 'failed')
      ) {
        _accumChild(item.root_trace_id, item);
      }
      // Opcao B (Teste da Morte H) — preservado: quando onlyRoot=true (default),
      // filhas nao entram em `activity` pra nao poluir a tela.
      const filters = get().activityFilters;
      const parentTraceId = row['parent_trace_id'];
      // handler_executions: only_root no RPC filtra por parent_handler_execution_id
      // (handler filho de outro handler) — espelha aqui no realtime.
      const isChildRow =
        (parentTraceId !== null && parentTraceId !== undefined) ||
        (table === 'handler_executions' && row['parent_handler_execution_id'] != null);
      if (filters.onlyRoot && isChildRow) {
        // Filha descartada do feed visivel; mas agendamos recalc dos pais running.
        _schedulePartialRecalc();
        return;
      }
      set((state) => {
        const existingIdx = state.activity.findIndex((x) => x.id === item.id);
        if (existingIdx >= 0) {
          const next = state.activity.slice();
          next[existingIdx] = item;
          return { activity: next };
        }
        return { activity: clampActivity([item, ...state.activity]) };
      });
      // Pai pode ter mudado status terminal → recalc limpa partials.
      _schedulePartialRecalc();
    };

    // Separa em channels independentes por tabela (espelha pattern de tasks que funciona).
    // Multiple postgres_changes listeners no mesmo channel pode causar bug de
    // matching no Supabase Realtime. Channel name unico por tabela tambem ajuda.
    // W1.4: agent_executions NAO entra aqui — particionada (relkind='p'), o
    // Realtime rejeita a assinatura (CHANNEL_ERROR). Coberta por polling.
    const makeChannel = (
      table:
        | 'skill_executions'
        | 'ephemeral_executions'
        | 'handler_executions'
        | 'companion_activity_log',
    ) => {
      const ch = supabase
        .channel(`activity-${table}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table, filter: `tenant_id=eq.${tenantId}` },
          handlePayload(table),
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            const prev = get().activityChannelState;
            set({ activityChannelState: 'joined' });
            if (prev === 'disconnected' || prev === 'error') {
              void get().fetchActivity(true);
            }
          } else if (status === 'CLOSED' || status === 'TIMED_OUT') {
            set({ activityChannelState: 'disconnected' });
          } else if (status === 'CHANNEL_ERROR') {
            set({ activityChannelState: 'error' });
          }
        });
      return ch;
    };

    _activityChannels.skill = makeChannel('skill_executions');
    _activityChannels.ephemeral = makeChannel('ephemeral_executions');
    // Reforma UX — tabelas novas na publication (backend). Se a publication
    // ainda não as inclui (pré-restart), o channel simplesmente não recebe
    // eventos — degrade graceful, sem erro.
    _activityChannels.handler = makeChannel('handler_executions');
    _activityChannels.companion = makeChannel('companion_activity_log');

    // Polling incremental cobre agent_executions (e qualquer evento perdido
    // pelos channels). Só existe enquanto Dashboard/Atividade está montado;
    // usa watermark obrigatório, pausa em background e recua em erro.
    startActivityPolling();
  },

  unsubscribeActivityRealtime: () => {
    stopActivityPolling();
    for (const key of ['skill', 'ephemeral', 'handler', 'companion'] as const) {
      const ch = _activityChannels[key];
      if (ch) {
        supabase.removeChannel(ch);
        _activityChannels[key] = null;
      }
    }
    if (_activityChannel) {
      supabase.removeChannel(_activityChannel);
      _activityChannel = null;
    }
    if (_metaRefreshTimer) {
      clearInterval(_metaRefreshTimer);
      _metaRefreshTimer = null;
    }
    if (_missBatch.timer) {
      clearTimeout(_missBatch.timer);
      _missBatch = { agentKeys: new Set(), skillIds: new Set(), timer: null };
    }
    set({ activityChannelState: 'idle' });
  },

  openActivityDetail: async (item: ActivityItem) => {
    set({
      selectedActivity: item,
      selectedActivityDetail: null,
      selectedActivityDetailLoading: true,
    });
    try {
      const resp = await api.getActivityDetail(item.kind, item.id);
      set({
        selectedActivityDetail: resp.item,
        selectedActivityDetailLoading: false,
      });
    } catch (err) {
      console.error('Failed to fetch activity detail:', err);
      set({ selectedActivityDetailLoading: false });
    }
  },

  closeActivityDetail: () => {
    set({
      selectedActivity: null,
      selectedActivityDetail: null,
      selectedActivityDetailLoading: false,
    });
  },
}));

/* Test-only exports — usados por mc-store-activity.test.ts */
export const __testHooks = {
  _agentsMeta,
  _skillsMeta,
  _metaInflight,
  // PR-5: helpers expostos pra testar realtime SUM dinamico.
  _childrenSums,
  recalcRunningParents,
  // W1.4: helpers expostos pra testar polling incremental (agent_executions
  // fora do realtime — tabela particionada).
  mergeActivityFeedItems,
  pollActivityFeedOnce,
  getActivityPollTimer: () => _activityPollTimer,
  getActivityPollFailures: () => _activityPollFailures,
  isActivityPollingActive: () => _activityPollingActive,
  resetCache: () => {
    _agentsMeta.clear();
    _skillsMeta.clear();
    resetMetaCache();
    _metaInflight.clear();
    _childrenSums.clear();
    _metaHydratedAt = null;
    stopActivityPolling();
    if (_metaRefreshTimer) {
      clearInterval(_metaRefreshTimer);
      _metaRefreshTimer = null;
    }
    if (_missBatch.timer) {
      clearTimeout(_missBatch.timer);
    }
    if (_partialRecalcTimer) {
      clearTimeout(_partialRecalcTimer);
      _partialRecalcTimer = null;
    }
    _missBatch = { agentKeys: new Set(), skillIds: new Set(), timer: null };
  },
  getMetaHydratedAt: () => _metaHydratedAt,
};
